import React, { useState } from 'react';
import './PrepareOlymp.css';

const PrepareOlymp = () => {
  const [file, setFile] = useState(null);
  const [message, setMessage] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [validationResult, setValidationResult] = useState(null);
  const [isValidating, setIsValidating] = useState(false);

  const BASEURL = process.env.REACT_APP_API_URL

  const handleFileChange = (e) => {
    const selectedFile = e.target.files[0];
    setFile(selectedFile);
    setMessage('');
    setValidationResult(null);
    
    if (selectedFile) {
      validateFile(selectedFile);
    }
  };

  const validateFile = async (file) => {
    setIsValidating(true);
    setMessage('Validating file format...');
    
    try {
      const formData = new FormData();
      formData.append('file', file);
      
      const token = localStorage.getItem('access_token');
      const response = await fetch(`${BASEURL}/prepare-olymp/validate`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`
        },
        body: formData
      });
      
      if (response.ok) {
        const result = await response.json();
        setValidationResult(result);
        setMessage(`✓ File format is valid. Found ${result.row_count} students.`);
      } else {
        const result = await response.json();
        setValidationResult({ valid: false, error: result.error });
        setMessage(`✗ ${result.error}`);
      }
    } catch (error) {
      console.error('Validation error:', error);
      setValidationResult({ valid: false, error: 'Network error' });
      setMessage(`Error validating file: ${error.message}`);
    } finally {
      setIsValidating(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!file) {
      setMessage('Please select an Excel file');
      return;
    }

    if (!validationResult || !validationResult.valid) {
      setMessage('Please select a valid Excel file with the correct format');
      return;
    }

    setIsLoading(true);
    setMessage('Processing file and preparing Olymp list...');

    try {
      const formData = new FormData();
      formData.append('file', file);
      
      const token = localStorage.getItem('access_token');
      const response = await fetch(`${BASEURL}/prepare-olymp`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`
        },
        body: formData
      });

      if (response.ok) {
        // Get the filename from the response headers
        const contentDisposition = response.headers.get('Content-Disposition');
        let filename = 'olymp_prepared.xlsx';
        if (contentDisposition) {
          const filenameMatch = contentDisposition.match(/filename="(.+)"/);
          if (filenameMatch) {
            filename = filenameMatch[1];
          }
        }

        // Create blob and download
        const blob = await response.blob();
        const url = window.URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.style.display = 'none';
        a.href = url;
        a.download = filename;
        document.body.appendChild(a);
        a.click();
        window.URL.revokeObjectURL(url);
        document.body.removeChild(a);

        setMessage('✓ Olymp list prepared successfully! File downloaded.');
      } else {
        const errorData = await response.json();
        setMessage(`Error: ${errorData.error}`);
      }
    } catch (error) {
      setMessage('Error processing file. Please try again.');
      console.error('Upload error:', error);
    } finally {
      setIsLoading(false);
    }
  };

  const renderValidationInfo = () => {
    if (!validationResult) return null;

    if (validationResult.valid) {
      return (
        <div className="validation-success">
          <h4>✓ File Validation Successful</h4>
          <div className="validation-details">
            <p><strong>Students found:</strong> {validationResult.row_count}</p>
            <p><strong>Columns detected:</strong> {validationResult.found_columns.join(', ')}</p>
            
            {validationResult.preview_data && validationResult.preview_data.length > 0 && (
              <div className="preview-section">
                <h5>Preview (first 5 rows):</h5>
                <div className="preview-table-container">
                  <table className="preview-table">
                    <thead>
                      <tr>
                        {validationResult.found_columns.map(col => (
                          <th key={col}>{col}</th>
                        ))}
                      </tr>
                    </thead>
                    <tbody>
                      {validationResult.preview_data.map((row, index) => (
                        <tr key={index}>
                          {validationResult.found_columns.map(col => (
                            <td key={col}>{row[col] || ''}</td>
                          ))}
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            )}
          </div>
        </div>
      );
    } else {
      return (
        <div className="validation-error">
          <h4>✗ File Validation Failed</h4>
          <p>{validationResult.error}</p>
          {validationResult.required_columns && (
            <div className="required-columns">
              <p><strong>Required columns:</strong> {validationResult.required_columns.join(', ')}</p>
              {validationResult.found_columns && (
                <p><strong>Found columns:</strong> {validationResult.found_columns.join(', ')}</p>
              )}
            </div>
          )}
        </div>
      );
    }
  };

  return (
    <div className="prepare-olymp-container">
      <div className="page-header">
        <h2>Prepare List to Olymp</h2>
        <p className="page-description">
          Upload an Excel file with student data to prepare it for Olymp format. 
          The file should contain columns: OrderID, Block, Name, DateOfBirth, CityBirth, Email.
        </p>
      </div>

      <div className="upload-section">
        <form onSubmit={handleSubmit}>
          <div className="file-input-group">
            <label htmlFor="excel-file">
              <span className="label-text">Select Excel File:</span>
              <input 
                id="excel-file"
                type="file" 
                accept=".xlsx,.xls" 
                onChange={handleFileChange} 
                required
                disabled={isLoading || isValidating}
              />
            </label>
            {file && (
              <div className="file-info">
                <span className="file-name">📄 {file.name}</span>
                <span className="file-size">({(file.size / 1024 / 1024).toFixed(2)} MB)</span>
              </div>
            )}
          </div>

          {isValidating && (
            <div className="validation-loading">
              <div className="spinner"></div>
              <span>Validating file format...</span>
            </div>
          )}

          {renderValidationInfo()}

          <div className="button-group">
            <button 
              type="submit" 
              disabled={isLoading || isValidating || !validationResult?.valid}
              className="process-button"
            >
              {isLoading ? (
                <>
                  <div className="spinner"></div>
                  Processing...
                </>
              ) : (
                'Process & Download Olymp List'
              )}
            </button>
          </div>
          
          {message && (
            <div className={`message ${
              message.includes('✓') ? 'success' : 
              message.includes('✗') ? 'error' : 
              'info'
            }`}>
              {message}
            </div>
          )}
        </form>
      </div>

      <div className="format-info">
        <h3>Expected Input Format</h3>
        <div className="format-details">
          <p>Your Excel file should contain the following columns:</p>
          <ul>
            <li><strong>OrderID</strong> - Unique identifier for the exam</li>
            <li><strong>Block</strong> - Module name (e.g., Hören, Lesen, Schreiben, Sprechen)</li>
            <li><strong>Name</strong> - Full name (will be split into first and last name)</li>
            <li><strong>DateOfBirth</strong> - Student's date of birth</li>
            <li><strong>CityBirth</strong> - Student's city of birth</li>
            <li><strong>Email</strong> - Student's email address</li>
          </ul>
          <p className="note">
            <strong>Note:</strong> Students with multiple modules will be grouped by email address in the output file.
          </p>
        </div>

        <h3>Output Format</h3>
        <div className="format-details">
          <p>The processed file will contain these columns:</p>
          <ul>
            <li>*id der Prüfung (nur in der ersten Zeile notwendig)</li>
            <li>Hören(CE05020815) - marked with 'X' if student has this module</li>
            <li>Lesen(CE05020816) - marked with 'X' if student has this module</li>
            <li>Schreiben(CE05020817) - marked with 'X' if student has this module</li>
            <li>Sprechen(CE05020818) - marked with 'X' if student has this module</li>
            <li>*Vorname, *Nachname, *Geburtsdatum, *Geburtsort, E-Mail-Adresse</li>
            <li>Ehegattennachzug, PASCH-Schule, PTN-Nummer (left blank)</li>
          </ul>
        </div>
      </div>
    </div>
  );
};

export default PrepareOlymp;
